import { Injectable, inject, signal, computed } from '@angular/core';
import { Subject, catchError, of, tap, takeUntil } from 'rxjs';
import { CaseService, Person } from '../case-service/case.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ConfigService } from '../../core/config.service';
import { formatDateToMDYWithTime, formatMDYWithTimeToISO } from '../../shared-components/utils/date.util';
import { getHttpErrorMessage } from '../../shared-components/utils/http-error-message.util';

export interface RouteToOption {
  value: string;
  label: string;
}

interface LetterRecipient {
  mailToName: string;
  mailToAddress: string;
  mailToCityStZip: string;
  mailToSalutation: string;
}

interface GenerateLetterRequest {
  actionCode: string | null;
  caseNo: string;
  routeToUser: number | null;
  actionDate: string | null;
  comments: string;
  recipients: LetterRecipient[];
}

export interface GeneratedLetterResponse {
  actionCode: string;
  caseNo: string;
  fileName: string;
  filePath: string;
  recipientCount: number;
  generatedAt: string;
}

interface ApiEnvelope<T> {
  status: string;
  statusCode: number;
  errorMessage: string | null;
  data: T;
  correlationId: string;
}

@Injectable({
  providedIn: 'root',
})
export class ActionService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly baseUrl = `${this.configService.get().APIBaseUrl}/search`;

  private readonly caseService = inject(CaseService);

  // Form state
  readonly selectedAction = signal<string | null>(null);
  readonly routeTo = signal<number | null>(null);
  readonly actionDate = signal<string | null>(null);
  readonly comments = signal<string>('');
  readonly selectedPeopleForAction = signal<Person[]>([]);
  readonly selectedActionObj = signal<any>(null);

  // Letter banner / request state
  readonly generatedLetter = signal<GeneratedLetterResponse | null>(null);
  readonly isGeneratingLetter = signal<boolean>(false);

  readonly routeToOptions: RouteToOption[] = [];

  readonly isValid = computed(() => !!this.selectedAction());

  // FIX: Use Subject to control subscription lifetime
  private generateLetterCancel$ = new Subject<void>();

  setSelectedAction(value: string | null): void {
    this.selectedAction.set(value);
  }

  setRouteTo(value: number | null): void {
    this.routeTo.set(value);
  }

  setActionDate(value: string | Date | null): void {
    if (value instanceof Date) {
      this.actionDate.set(formatDateToMDYWithTime(value));
    } else {
      this.actionDate.set(value);
    }
  }

  setComments(value: string): void {
    this.comments.set(value);
  }

  setSelectedPeople(people: Person[]): void {
    this.selectedPeopleForAction.set(people);
  }

  setSelectedActionObj(action: any): void {
    this.selectedActionObj.set(action);
  }

  clearSelectedActionOnCancel(): void {
    if (this.selectedPeopleForAction().length === 0) {
      this.selectedAction.set(null);
    }
  }

  private formatDateToISO(dateStr: string | null): string | null {
    return formatMDYWithTimeToISO(dateStr);
  }

  private buildPayload(caseId: string): GenerateLetterRequest {
    return {
      actionCode: this.selectedAction(),
      caseNo: caseId,
      routeToUser: this.routeTo(),
      actionDate: this.formatDateToISO(this.actionDate()),
      comments: this.comments(),
      recipients: this.selectedPeopleForAction().map((p) => ({
        mailToName: p.name,
        mailToAddress: p.address,
        mailToCityStZip: p.cityStateZip,
        mailToSalutation: p.mailToSalutation
      })),
    };
  }

  private generateLetter(payload: GenerateLetterRequest) {
    return this.http.post<ApiEnvelope<GeneratedLetterResponse>>(
      `${this.baseUrl}/generate-letter`,
      payload,
    );
  }

  createLetter(caseId: string): boolean {
    if (!this.selectedAction()) {
      this.caseService.showToast(
        'Validation Error: The "Action" selection field is required to proceed.',
        'error',
      );
      return false;
    }

    // FIX: Cancel any previous request
    this.generateLetterCancel$.next();

    const payload = this.buildPayload(caseId);
    this.isGeneratingLetter.set(true);

    // FIX: Use takeUntil to automatically unsubscribe
    this.generateLetter(payload)
      .pipe(
        takeUntil(this.generateLetterCancel$), // ← Unsubscribe when cancel$ emits
        tap((response) => {
          this.isGeneratingLetter.set(false);

          if (response.status !== 'SUCCESS') {
            this.caseService.showToast(
              response.errorMessage || 'Failed to generate document. Please try again.',
              'error',
              false,
              response.correlationId,
            );
            return;
          }

          if (!response.data) {
            this.caseService.showToast(
              'Failed to generate document. Please try again.',
              'error',
              false,
              response.correlationId,
            );
            return;
          }

          this.generatedLetter.set(response.data);
          if (!response.data.filePath) {
            this.caseService.showToast(
              'Document generated, but no file path was returned.',
              'error',
              false,
              response.correlationId,
            );
            return;
          }
          this.caseService.showToast(`Document saved successfully for Case #${caseId}!`, 'success');
        }),
        catchError((err: HttpErrorResponse) => {
          this.isGeneratingLetter.set(false);

          const message = getHttpErrorMessage(err, {
            service: 'letter generation service',
            operation: 'generating the letter',
            fallback: 'Failed to generate document. Please try again.',
          });
          this.caseService.showToast(message, 'error', false, err.error?.correlationId);

          return of(null);
        }),
      )
      .subscribe();

    return true;
  }

  openGeneratedLetter(): void {
    const letter = this.generatedLetter();

    if (!letter?.filePath) {
      this.caseService.showToast('No generated document is available to open.', 'error');
      return;
    }

    if (!this.openInWord(letter.filePath)) {
      this.caseService.showToast(
        'Could not open the document in Word. You can open it manually from the shown path.',
        'error',
      );
    }
  }

  private openInWord(documentTarget: string): boolean {
    const url = this.toOpenableUrl(documentTarget);
    if (!url) {
      return false;
    }

    window.location.href = `ms-word:ofe|u|${url}`;
    return true;
  }

  private toOpenableUrl(documentTarget: string): string | null {
    const target = documentTarget?.trim();
    if (!target) {
      return null;
    }

    if (/^https?:\/\//i.test(target) || /^file:\/\//i.test(target)) {
      return target;
    }

    const isDrivePath = /^[a-zA-Z]:[\\/]/.test(target);
    const isUncPath = target.startsWith('\\\\');
    if (isDrivePath || isUncPath) {
      return this.toFileUrl(target);
    }

    return null;
  }

  private toFileUrl(path: string): string {
    const normalized = path.replace(/\\/g, '/');

    // UNC path: \\server\share\file -> file://server/share/file
    if (normalized.startsWith('//')) {
      const [server, ...segments] = normalized.slice(2).split('/');
      const encodedSegments = segments.map((segment) => encodeURIComponent(segment)).join('/');
      return `file://${encodeURIComponent(server)}/${encodedSegments}`;
    }

    // Drive path: D:\folder\file -> file:///D:/folder/file
    const driveMatch = normalized.match(/^([a-zA-Z]):\/(.*)$/);
    if (driveMatch) {
      const drive = driveMatch[1];
      const encodedSegments = driveMatch[2]
        .split('/')
        .map((segment) => encodeURIComponent(segment))
        .join('/');
      return `file:///${drive}:/${encodedSegments}`;
    }

    return `file:///${normalized}`;
  }

  closeBanner(): void {
    this.generatedLetter.set(null);
  }

  reset(): void {
    // FIX: Cancel any pending request
    this.generateLetterCancel$.next();

    this.selectedAction.set(null);
    this.routeTo.set(null);
    this.actionDate.set(null);
    this.comments.set('');
    this.selectedPeopleForAction.set([]);
    this.selectedActionObj.set(null);
    this.generatedLetter.set(null);
    this.isGeneratingLetter.set(false);
  }

  // FIX: Cleanup method for app shutdown
  cleanup(): void {
    // Cancel any pending requests
    this.generateLetterCancel$.next();
    this.generateLetterCancel$.complete();

    // Reset all signals
    this.reset();
  }
}